import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, X, Eye, EyeOff, Mail, Send, Copy } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import BulkActionBar, { SelectCheckbox } from "@/components/BulkActionBar";

const EMPTY = {
    type: "saison",
    titre: "",
    contenu: "",
    image: "",
    prix: "",
    unite: "kg",
    date_debut: "",
    date_fin: "",
    published: true,
    notify_by_email: true,
};

function toPayload(form) {
    return {
        type: form.type,
        titre: form.titre,
        contenu: form.contenu,
        image: form.image?.trim() ? form.image.trim() : null,
        prix: form.prix === "" || form.prix == null ? null : Number(form.prix),
        unite: form.unite || "kg",
        date_debut: form.date_debut || null,
        date_fin: form.date_fin || null,
        published: !!form.published,
        notify_by_email: !!form.notify_by_email,
    };
}

function fromSuggestion(s) {
    return {
        type: s.type || "saison",
        titre: s.titre || "",
        contenu: s.contenu || "",
        image: s.image || "",
        prix: s.prix == null ? "" : s.prix,
        unite: s.unite || "kg",
        date_debut: s.date_debut || "",
        date_fin: s.date_fin || "",
        published: !!s.published,
        notify_by_email: false, // never re-notify by default on edit
    };
}

function formatRelative(iso) {
    if (!iso) return null;
    try {
        const then = new Date(iso);
        const diff = (Date.now() - then.getTime()) / 1000;
        if (diff < 60) return `il y a ${Math.round(diff)} s`;
        if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
        if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
        return `le ${then.toLocaleDateString("fr-CH", { day: "numeric", month: "short" })}`;
    } catch {
        return iso.slice(0, 10);
    }
}

export default function AdminSuggestions() {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(null); // suggestion object or 'new'
    const [form, setForm] = useState(EMPTY);
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    const { data: suggestions = [], isLoading } = useQuery({
        queryKey: ["admin-suggestions"],
        queryFn: async () => (await api.get("/admin/suggestions")).data,
    });

    const invalidateAll = () => {
        qc.invalidateQueries({ queryKey: ["admin-suggestions"] });
        qc.invalidateQueries({ queryKey: ["suggestions"] });
    };

    const create = useMutation({
        mutationFn: (data) => api.post("/admin/suggestions", data),
        onSuccess: () => {
            toast.success("Suggestion créée");
            setEditing(null);
            invalidateAll();
        },
        onError: (e) => toast.error(formatApiError(e)),
    });
    const update = useMutation({
        mutationFn: ({ id, data }) => api.put(`/admin/suggestions/${id}`, data),
        onSuccess: () => {
            toast.success("Suggestion enregistrée");
            setEditing(null);
            invalidateAll();
        },
        onError: (e) => toast.error(formatApiError(e)),
    });
    const remove = useMutation({
        mutationFn: (id) => api.delete(`/admin/suggestions/${id}`),
        onSuccess: () => {
            toast.success("Suggestion supprimée");
            invalidateAll();
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const resend = useMutation({
        mutationFn: (id) => api.post(`/admin/suggestions/${id}/send-email`),
        onSuccess: () => {
            toast.success("Email en cours d'envoi aux pros validés.");
            invalidateAll();
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const duplicate = useMutation({
        mutationFn: (source) => {
            const payload = {
                type: source.type || "saison",
                titre: `${source.titre || "Suggestion"} (copie)`,
                contenu: source.contenu || "",
                image: source.image?.trim() ? source.image.trim() : null,
                prix: source.prix == null ? null : Number(source.prix),
                unite: source.unite || "kg",
                date_debut: source.date_debut || null,
                date_fin: source.date_fin || null,
                published: false, // always a draft — admin edits then publishes
                notify_by_email: false, // never re-notify on duplication
            };
            return api.post("/admin/suggestions", payload);
        },
        onSuccess: () => {
            toast.success("Suggestion dupliquée en brouillon.");
            invalidateAll();
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const bulkDelete = useMutation({
        mutationFn: (ids) => api.post("/admin/suggestions/bulk-delete", { ids }),
        onSuccess: (res) => {
            toast.success(`${res.data?.deleted ?? 0} suggestion(s) supprimée(s)`);
            setSelectedIds(new Set());
            invalidateAll();
        },
        onError: (e) => toast.error(formatApiError(e)),
    });

    const toggleSelect = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const allSelected = suggestions.length > 0 && suggestions.every((s) => selectedIds.has(s.id));
    const toggleAll = () => {
        if (allSelected) setSelectedIds(new Set());
        else setSelectedIds(new Set(suggestions.map((s) => s.id)));
    };
    const clearSelection = () => setSelectedIds(new Set());
    const onBulkDelete = () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (window.confirm(`Supprimer définitivement ${ids.length} suggestion${ids.length > 1 ? "s" : ""} ?`)) {
            bulkDelete.mutate(ids);
        }
    };

    const startNew = () => {
        setForm(EMPTY);
        setEditing("new");
    };
    const startEdit = (s) => {
        setForm(fromSuggestion(s));
        setEditing(s);
    };
    const cancel = () => {
        setEditing(null);
        setForm(EMPTY);
    };
    const save = () => {
        if (!form.titre.trim() || !form.contenu.trim()) {
            toast.error("Titre et contenu sont obligatoires");
            return;
        }
        if (form.date_debut && form.date_fin && form.date_fin < form.date_debut) {
            toast.error("La date de fin doit être après la date de début");
            return;
        }
        const payload = toPayload(form);
        if (editing === "new") create.mutate(payload);
        else update.mutate({ id: editing.id, data: payload });
    };
    const togglePublish = (s) => {
        update.mutate({ id: s.id, data: toPayload({ ...fromSuggestion(s), published: !s.published }) });
    };

    return (
        <div data-testid="admin-suggestions">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h2 className="font-serif text-2xl text-[#D4BC7C]">Suggestions du moment</h2>
                    <p className="text-sm text-[#A0A0A0] mt-2">
                        Publiez les suggestions de saison avec leur prix HT et la semaine concernée.
                    </p>
                </div>
                <button className="bc-btn bc-btn--sm" onClick={startNew} data-testid="btn-new-suggestion">
                    <Plus size={14} /> Nouvelle suggestion
                </button>
            </div>

            {editing && (
                <div className="bc-card mt-8" data-testid="suggestion-form">
                    <div className="flex items-center justify-between">
                        <p className="bc-label">{editing === "new" ? "Nouvelle suggestion" : "Modifier"}</p>
                        <button className="text-[#A0A0A0] hover:text-[#D4BC7C]" onClick={cancel} data-testid="sug-cancel">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="mt-4">
                        <label className="bc-label">Titre</label>
                        <input
                            className="bc-input"
                            value={form.titre}
                            onChange={(e) => setForm({ ...form, titre: e.target.value })}
                            data-testid="sug-titre"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="bc-label">Date de début (semaine du…)</label>
                            <input
                                type="date"
                                className="bc-input"
                                value={form.date_debut}
                                onChange={(e) => setForm({ ...form, date_debut: e.target.value })}
                                data-testid="sug-date-debut"
                            />
                        </div>
                        <div>
                            <label className="bc-label">Date de fin (… au)</label>
                            <input
                                type="date"
                                className="bc-input"
                                value={form.date_fin}
                                onChange={(e) => setForm({ ...form, date_fin: e.target.value })}
                                data-testid="sug-date-fin"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-[1fr,140px] gap-4 mt-4">
                        <div>
                            <label className="bc-label">Prix HT (laisser vide = pas de prix affiché)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="bc-input"
                                placeholder="Ex : 62.00"
                                value={form.prix}
                                onChange={(e) => setForm({ ...form, prix: e.target.value })}
                                data-testid="sug-prix"
                            />
                        </div>
                        <div>
                            <label className="bc-label">Unité</label>
                            <select
                                className="bc-input"
                                value={form.unite}
                                onChange={(e) => setForm({ ...form, unite: e.target.value })}
                                data-testid="sug-unite"
                            >
                                <option value="kg">/ kg</option>
                                <option value="pce">/ pièce</option>
                            </select>
                        </div>
                    </div>

                    <div className="mt-4">
                        <label className="bc-label">Description</label>
                        <textarea
                            className="bc-input"
                            rows={5}
                            value={form.contenu}
                            onChange={(e) => setForm({ ...form, contenu: e.target.value })}
                            data-testid="sug-contenu"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="bc-label">Image</label>
                        <ImageUpload
                            value={form.image || ""}
                            onChange={(next) => setForm({ ...form, image: next || "" })}
                            testId="sug-image-upload"
                        />
                        <div className="mt-3">
                            <label className="text-[10px] text-[#A0A0A0] uppercase tracking-widest">
                                Ou coller une URL d&apos;image (optionnel)
                            </label>
                            <input
                                className="bc-input mt-1"
                                placeholder="https://…"
                                value={form.image && form.image.startsWith("data:") ? "" : (form.image || "")}
                                onChange={(e) => setForm({ ...form, image: e.target.value })}
                                data-testid="sug-image"
                            />
                        </div>
                    </div>

                    <div className="mt-4 flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.published}
                                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                                className="h-4 w-4 accent-[#D4BC7C]"
                                data-testid="sug-published"
                            />
                            <span className="text-sm text-[#F2ECE0]">Publié (visible côté pro)</span>
                        </label>
                    </div>

                    <div className="mt-3 p-3 border" style={{ borderColor: "rgba(212,188,124,0.3)", background: "rgba(212,188,124,0.05)" }}>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.notify_by_email}
                                onChange={(e) => setForm({ ...form, notify_by_email: e.target.checked })}
                                className="h-4 w-4 accent-[#D4BC7C] mt-0.5"
                                data-testid="sug-notify"
                            />
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <Mail size={14} className="text-[#D4BC7C]" />
                                    <span className="text-sm text-[#F2ECE0] font-medium">Notifier les pros par email</span>
                                </div>
                                <p className="text-xs text-[#A0A0A0] mt-1">
                                    Un email est envoyé aux pros validés qui ont activé les notifications, uniquement si la suggestion est publiée.
                                </p>
                            </div>
                        </label>
                    </div>

                    <div className="mt-6 flex gap-3">
                        <button className="bc-btn" onClick={save} data-testid="sug-save">
                            <Save size={14} /> Enregistrer
                        </button>
                        <button className="bc-btn bc-btn--ghost" onClick={cancel}>
                            Annuler
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-8">
                <BulkActionBar
                    selectedCount={selectedIds.size}
                    totalCount={suggestions.length}
                    allSelected={allSelected}
                    onToggleAll={toggleAll}
                    onClear={clearSelection}
                    onBulkDelete={onBulkDelete}
                    disabled={bulkDelete.isPending}
                    label="suggestion"
                />
            </div>

            <div className="mt-6 space-y-4">
                {isLoading && <p className="text-[#A0A0A0]">Chargement...</p>}
                {suggestions.map((s) => (
                    <div key={s.id} className="bc-card" data-testid={`suggestion-item-${s.id}`}>
                        <div className="flex justify-between items-start gap-4 flex-wrap">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className="pt-1">
                                    <SelectCheckbox
                                        checked={selectedIds.has(s.id)}
                                        onChange={(v) => toggleSelect(s.id, v)}
                                        testId={`select-suggestion-${s.id}`}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span
                                            className="text-[10px] uppercase tracking-widest px-2 py-0.5 border"
                                            style={{
                                                color: s.published ? "#D4BC7C" : "#A0A0A0",
                                                borderColor: s.published
                                                    ? "var(--bc-gold)"
                                                    : "var(--bc-border)",
                                            }}
                                        >
                                            {s.published ? "Publié" : "Brouillon"}
                                        </span>
                                        {s.date_debut && s.date_fin && (
                                            <span className="text-[10px] uppercase tracking-widest text-[#A0A0A0]">
                                                {s.date_debut} → {s.date_fin}
                                            </span>
                                        )}
                                        {s.prix != null && (
                                            <span className="text-[10px] uppercase tracking-widest text-[#D4BC7C] font-medium">
                                                CHF {Number(s.prix).toFixed(2)} / {s.unite || "kg"}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="font-serif text-[#D4BC7C] text-xl mt-2">{s.titre}</h3>
                                    <p className="text-sm text-[#F2ECE0]/80 mt-2 line-clamp-3 whitespace-pre-line">
                                        {s.contenu}
                                </p>
                            </div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    className="bc-btn bc-btn--sm bc-btn--ghost"
                                    onClick={() => togglePublish(s)}
                                    data-testid={`toggle-${s.id}`}
                                >
                                    {s.published ? <EyeOff size={14} /> : <Eye size={14} />}
                                    {s.published ? "Dépublier" : "Publier"}
                                </button>
                                {s.published && (
                                    <button
                                        className="bc-btn bc-btn--sm bc-btn--ghost"
                                        onClick={() => {
                                            if (window.confirm("Envoyer cette suggestion par email à tous les pros validés ?")) {
                                                resend.mutate(s.id);
                                            }
                                        }}
                                        disabled={resend.isPending}
                                        data-testid={`resend-${s.id}`}
                                        title="Envoyer par email aux pros"
                                    >
                                        <Send size={14} /> Envoyer par email
                                    </button>
                                )}
                                <button className="bc-btn bc-btn--sm" onClick={() => startEdit(s)} data-testid={`edit-${s.id}`}>
                                    Modifier
                                </button>
                                <button
                                    className="bc-btn bc-btn--sm bc-btn--ghost"
                                    onClick={() => duplicate.mutate(s)}
                                    disabled={duplicate.isPending}
                                    data-testid={`duplicate-${s.id}`}
                                    title="Créer une copie en brouillon"
                                >
                                    <Copy size={14} /> Dupliquer
                                </button>
                                <button
                                    className="bc-btn bc-btn--sm bc-btn--danger"
                                    onClick={() => {
                                        if (window.confirm("Supprimer cette suggestion ?")) remove.mutate(s.id);
                                    }}
                                    data-testid={`delete-sug-${s.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                        {s.last_email_sent_at && (
                            <p className="text-xs text-[#7a7a7a] mt-4 flex items-center gap-1.5" data-testid={`email-status-${s.id}`}>
                                <Mail size={11} /> Dernier envoi email {formatRelative(s.last_email_sent_at)}
                                {s.last_email_recipients_count != null && ` · ${s.last_email_recipients_count} destinataire${s.last_email_recipients_count > 1 ? "s" : ""}`}
                            </p>
                        )}
                    </div>
                ))}
                {!isLoading && suggestions.length === 0 && (
                    <p className="text-[#A0A0A0]" data-testid="sug-empty">
                        Aucune suggestion.
                    </p>
                )}
            </div>
        </div>
    );
}
